import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { cn } from "../../../lib/utils";

import {
  getContentCollectMeta,
  isContentCollectAsset,
  type ContentCollectProvenanceSource,
} from "./content-collect-provenance";

/**
 * The "this came from a collect" mark on a Library card (CC-31 / SKE-4288).
 *
 * Sits bottom-right of the thumbnail, the one free corner: the used flag owns
 * bottom-left, the checkbox top-left, and the username badge and hover actions
 * top-right. It borrows the used flag's dark scrim rather than a brand colour —
 * a grid of collected assets shouldn't turn into a wall of blurple.
 *
 * Renders nothing for every other asset, so callers can mount it unconditionally.
 *
 * The prop is narrowed to the two fields the provenance test actually reads,
 * which is what `isContentCollectAsset` already accepts. A real
 * `CollectionFile` still satisfies it; the coded product mock on the Collect
 * upsell (SKE-4862) can satisfy it too, so the splash shows the SAME badge the
 * Library draws rather than a lookalike that can drift away from it.
 */
export function ContentCollectBadge({
  asset,
  className,
}: {
  asset: ContentCollectProvenanceSource;
  className?: string;
}) {
  if (!isContentCollectAsset(asset)) return null;

  const meta = getContentCollectMeta(asset);
  const collectName = meta?.collectName?.trim();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "absolute right-2 bottom-2 z-[2] flex max-w-[calc(100%-16px)] items-center gap-1.5",
            "rounded-md bg-black/50 px-2 py-1 font-inter text-xs font-medium text-white",
            className
          )}
          data-testid="content-collect-badge"
        >
          <i
            className="fa-regular fa-inbox-in text-[10px]"
            aria-hidden="true"
          />
          <span className="truncate">Content Collect</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-center text-xs">
        {collectName
          ? `Collected through “${collectName}”.`
          : "Collected through a Content Collect link."}
      </TooltipContent>
    </Tooltip>
  );
}
